import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import CategoryGrid from '../Components/Home/CategoryGrid';
import LocationBanner from '../Components/Home/LocationBanner';
import QuickMenu from '../Components/Home/QuickMenu';
import Layout from '../Components/Layout/Layout';
import NearbyAds from '../Components/Location/NearbyAds';
import Posts from '../Components/Posts/Posts';
import { LocationContext } from '../contextStore/LocationContext';
import { Firebase } from '../firebase/config';
import { seededShuffle } from '../utils/seededShuffle';
import './Home.css';

const QUICK_MENU_COUNT = 8;

function Home() {
  const { browseLocation } = useContext(LocationContext);
  const history = useHistory();
  const [visitSeed] = useState(() => Date.now());
  const [allProducts, setAllProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    setProductsLoading(true);
    Firebase.firestore()
      .collection('products')
      .where('status', '==', 'active')
      .orderBy('createdAt', 'desc')
      .get()
      .then((snapshot) => {
        const list = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }));
        setAllProducts(list);
      })
      .catch(() => setAllProducts([]))
      .finally(() => setProductsLoading(false));
  }, []);

  const handleSearch = () => {
    if (searchInput.trim()) {
      history.push(`/search?q=${encodeURIComponent(searchInput.trim())}`);
    }
  };

  const freshProducts = seededShuffle(allProducts, visitSeed);

  // Filtered Lineups
  const mensLineup = allProducts.filter(p => p.category?.toLowerCase().includes('men')).slice(0, 4);
  const womensLineup = allProducts.filter(p => p.category?.toLowerCase().includes('women')).slice(0, 4);
  const boysLineup = allProducts.filter(p => p.category?.toLowerCase().includes('boy') || p.category?.toLowerCase().includes('junior')).slice(0, 4);

  return (
    <Layout>
      {/* Optimized Layout Inline Utilities - Keeping structural elements perfectly adaptive */}
      <style>{`
        .info-section-bg {
          background: #013220;
          color: #fff;
          padding: 50px 4%;
          text-align: center;
        }
        .info-header h2 {
          font-size: 1.8rem;
          margin-bottom: 10px;
        }
        .channel-container {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 20px;
          max-width: 1200px;
          margin: 30px auto 0;
        }
        .channel-card {
          background: #fff;
          color: #000;
          border-radius: 12px;
          padding: 20px;
          text-align: left;
          border-top: 5px solid #013220;
        }
        .channel-card h3 { color: #013220; margin: 10px 0 15px; font-size: 1.3rem; }
        .channel-card ul { list-style: none; padding: 0; }
        .channel-card li { margin-bottom: 12px; font-size: 0.95rem; display: flex; align-items: flex-start; }
        .channel-card li::before { content: "✓"; color: #28a745; font-weight: bold; margin-right: 10px; }

        .payment-steps { background: #f9f9f9; color: #000; padding: 50px 4%; text-align: center; }
        .steps-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 20px;
          max-width: 1200px;
          margin: 30px auto 0;
        }
        .step-box { background: #fff; border-radius: 10px; padding: 20px; text-align: left; box-shadow: 0 4px 6px rgba(0,0,0,0.05); }
        .step-box h4 { margin-bottom: 15px; font-size: 1.1rem; color: #013220; }
        .step-item { display: flex; align-items: center; margin-bottom: 12px; font-size: 0.9rem; }
        .step-num { background: #013220; color: #fff; width: 22px; height: 22px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 11px; margin-right: 12px; flex-shrink: 0; }

        @media(min-width: 768px) {
          .info-section-bg, .payment-steps { padding: 60px 6%; }
          .info-header h2 { font-size: 2.2rem; }
        }
      `}</style>

      <div className="homeParentDiv">
        <section className="hero">
          <div className="hero-content">
            <div className="hero-badge">PRE-OWNED GOLF MARKETPLACE</div>
            <h1>Upgrade Your Game For <span>Less.</span></h1>
            <p>Buy & sell high-quality golf equipment from India's most trusted community.</p>
            <div className="hero-btns">
              <button className="btn-gold" onClick={() => history.push('/search')}>Browse Gear</button>
            </div>
          </div>
        </section>

        <div className="search-strip">
          <input
            type="text"
            placeholder="Search for Drivers, Irons, Putters..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button className="btn-search" onClick={handleSearch}>Search</button>
        </div>

        <div className="section-padding">
          <div className="section-header"><h2>Browse by Category</h2></div>
          <CategoryGrid onSelectCategory={(name, id) => history.push(id ? `/category/${id}` : `/search?q=${encodeURIComponent(name)}`)} />
        </div>

        <div className="section-padding">
          <div className="section-header"><h2>Quick Picks</h2></div>
          <QuickMenu products={allProducts.slice(0, QUICK_MENU_COUNT)} loading={productsLoading} />
        </div>

        <section className="grade-system-box">
          <div className="grade-header" style={{ textAlign: 'center', marginBottom: '20px' }}>
            <h2 style={{ color: '#fff', fontSize: '1.6rem', marginBottom: '5px' }}>The ClubLoop Grade System</h2>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.95rem' }}>Standardized quality checks for every listing.</p>
          </div>
          <div className="grade-grid">
            <div className="grade-item"><span className="grade-badge aplus">A+</span><p>New / Mint</p></div>
            <div className="grade-item"><span className="grade-badge a">A</span><p>Excellent</p></div>
            <div className="grade-item"><span className="grade-badge b">B</span><p>Good</p></div>
            <div className="grade-item"><span className="grade-badge c">C</span><p>Fair</p></div>
          </div>
        </section>

        <div className="section-padding">
          <div className="section-header"><h2>Fresh Recommendations</h2></div>
          <Posts allPosts={allProducts} freshPosts={freshProducts} loading={productsLoading} />
        </div>

        {/* LINEUPS SECTIONS */}
        <div className="section-padding">
          <div className="section-header"><h2>Men's Equipment Lineup</h2></div>
          <QuickMenu products={mensLineup} loading={productsLoading} />
        </div>

        <div className="section-padding">
          <div className="section-header"><h2>Women's Equipment Lineup</h2></div>
          <QuickMenu products={womensLineup} loading={productsLoading} />
        </div>

        <div className="section-padding">
          <div className="section-header"><h2>Boys' Equipment Lineup</h2></div>
          <QuickMenu products={boysLineup} loading={productsLoading} />
        </div>

        {/* HOW IT WORKS */}
        <section className="info-section-bg">
          <div className="info-header">
            <p style={{ fontSize: '11px', fontWeight: 'bold', letterSpacing: '1px', opacity: 0.8 }}>SIMPLE PROCESS</p>
            <h2>How ClubLoop Works</h2>
          </div>
          <div className="channel-container">
            <div className="channel-card">
              <h3>Buy Direct from ClubLoop</h3>
              <ul>
                <li>Every item personally inspected & graded A+ to C</li>
                <li>Instant purchase — no waiting for a seller</li>
              </ul>
            </div>
            <div className="channel-card">
              <h3>Buy from Fellow Golfers</h3>
              <ul>
                <li>Seller verified before listing goes live</li>
                <li>Payment held by ClubLoop until delivery confirmed</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="payment-steps">
          <h2 style={{ color: '#013220', fontSize: '1.8rem', marginBottom: '10px' }}>How Payments Work</h2>
          <div className="steps-grid">
            <div className="step-box">
              <h4>🏪 Channel 1 — Buy from ClubLoop</h4>
              {["Choose item", "Pay ClubLoop", "Deliver/Inspect", "Deal Complete"].map((text, i) => (
                <div key={i} className="step-item"><span className="step-num">{i + 1}</span><p>{text}</p></div>
              ))}
            </div>
            <div className="step-box">
              <h4>🤝 Channel 2 — P2P Marketplace</h4>
              {["Match Buyer/Seller", "Buyer pays ClubLoop", "Seller delivers", "Confirm & Release"].map((text, i) => (
                <div key={i} className="step-item"><span className="step-num">{i + 1}</span><p>{text}</p></div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}

export default Home;
